import React, { useState, useEffect, useRef } from 'react';
import { SlFormatDate, SlIcon } from '@shoelace-style/shoelace/dist/react';
import { getMountain } from '../utils/moutainDetails';
import { ClaimEntryStyles } from './claim-entry.styles';
import claimedIcon from '../assets/blue_circle.svg';
import placeholderImage from '../assets/blue_circle.png';
import { useTranslation } from 'react-i18next';
import Carousel from 'nuka-carousel';
import { Link } from 'react-router-dom';


const ClaimEntryComponent = ({ claim }) => {
  const mountain = getMountain(claim.mountainId);
  const { t } = useTranslation('app');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const videoRefs = useRef({});
  const carouselContainerRef = useRef(null);

  // Reset on every claim change
  useEffect(() => {
    // Stop all videos
    Object.values(videoRefs.current).forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.load(); // Force reload
      }
    });

    // Reset all state
    setPlayingVideo(null);
    videoRefs.current = {};

    // Force component update
    setForceUpdate(Date.now());
  }, [claim.uuid]);

  const prepareMediaItems = () => {
    const items = [];

    // First check if we have the media
    if (claim.mediaUrls && claim.mediaUrls.length > 0) {
      claim.mediaUrls.forEach((media, index) => {
        if (media.type === 'image') {
          items.push({
            type: 'image',
            url: media.url,
            id: `${claim.uuid}-media-image-${index}-${forceUpdate}`
          });
        } else if (media.type === 'video') {
          items.push({
            type: 'video',
            url: media.url,
            thumbnailUrl: media.thumbnailUrl,
            id: `${claim.uuid}-media-video-${index}-${forceUpdate}`
          });
        }
      });
    }

    // Use placeholder if no media
    if (items.length === 0) {
      items.push({
        type: 'image',
        url: placeholderImage,
        id: `${claim.uuid}-placeholder-${forceUpdate}`
      });
    }

    return items;
  };

  const mediaItems = prepareMediaItems();

  // Handle slide change
  const handleSlideChange = (slideIndex) => {
    // Stop any playing videos when slide changes
    Object.values(videoRefs.current).forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setPlayingVideo(null);
  };

  // Handle video play
  const handleVideoPlay = (videoId) => {
    setPlayingVideo(videoId);

    // Play the video
    const video = videoRefs.current[videoId];
    if (video) {
      video.play();
    }
  };

  // Stop videos when component unmounts
  useEffect(() => {
    const currentVideos = videoRefs.current;
    return () => {
      Object.values(currentVideos).forEach(video => {
        if (video) {
          video.pause();
        }
      });
    };
  }, []);

  // Stop displaying carousel controls when a video is playing
  useEffect(() => {
    if (carouselContainerRef.current) {
      const carouselControls = carouselContainerRef.current.querySelector('.slider-control-bottomcenter');
      if (carouselControls) {
        carouselControls.style.display = playingVideo ? 'none' : 'block';
      }
    }
  }, [playingVideo]);

  return (
    <ClaimEntryStyles ref={carouselContainerRef}>
      <Carousel
        key={`carousel-${claim.uuid}-${forceUpdate}`}
        className={'image-carousel'}
        withoutControls={mediaItems.length <= 1}
        afterSlide={handleSlideChange}
      >
        {mediaItems.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'block',
              height: '250px',
              width: '100%',
              position: 'relative',
              background: 'lightgray',
            }}
          >
            {item.type === 'image' ? (
              <Link
                to={`/entries/${claim.uuid}`}
                style={{
                  display: 'block',
                  height: '100%',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <img
                  src={item.url}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: item.id.includes('placeholder') ? 'contain' : 'cover',
                    background: item.id.includes('placeholder') ? '#0168ff' : 'transparent'
                  }}
                  alt={claim.name}
                />
              </Link>
            ) : (
              <div style={{ position: 'relative', height: '100%', width: '100%' }}>
                {playingVideo !== item.id ? (
                  <>
                    {/* Thumbnail - no Link wrapper for video thumbnails */}
                    <img
                      src={item.thumbnailUrl || placeholderImage}
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      alt={`Video thumbnail ${claim.name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleVideoPlay(item.id);
                      }}
                    />
                    {/* Play button overlay */}
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleVideoPlay(item.id);
                      }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                      }}
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="white"
                        style={{ marginLeft: '1px' }}
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </>
                ) : (
                  /* Video player */
                  <video
                    key={`video-${item.id}`}
                    ref={(el) => videoRefs.current[item.id] = el}
                    src={item.url}
                    style={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                      background: '#000'
                    }}
                    controls
                    autoPlay
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseMove={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onPointerMove={(e) => e.stopPropagation()}
                    onPointerUp={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </Carousel>

      <Link
        to={`/entries/${claim.uuid}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <figcaption style={{ padding: '0.5rem 1rem 1rem' }}>
          <div className={'detail-header'}>
            <div className={'voted'}>
              <img src={claimedIcon} alt={'voted logo'} />
            </div>
            <div className={'keys'}>
              <span>
                <SlIcon name={'calendar'} />
                <SlFormatDate
                  date={claim.date}
                  month="long"
                  day="numeric"
                  year="numeric"
                  lang={'de'}
                />
              </span>
              <span>
                <SlIcon name={'geo-alt'} />
                {mountain.name}
              </span>
            </div>
          </div>
          <div className={'title'}>
            {t('mountain.voiceFrom')} <strong>{claim.firstName} {claim.lastName}</strong>
          </div>
          {claim.comment && <div className={'comment'}>{claim.comment}</div>}
        </figcaption>
      </Link>
    </ClaimEntryStyles>
  );
};

export default ClaimEntryComponent;